import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../../core/models/base-state/base-state';
import { getHealthSummary, setGlobalPageSize } from '../../../../../core/store/actions/app.actions';
import { PatientFamilyHistory } from '../../../models/patient-family-history';
import * as familyActions from '../actions/patient-family-history.actions';

export interface PatientFamilyHistoriesState extends EntityState<PatientFamilyHistory> {
  baseState: baseState.BaseState;
  pageSize: number;
  isFullDatasetLoaded: boolean;
  cachePatientId: number | null;
  cacheLoadedAt: number | null;
  isFullDatasetLoading: boolean;
  fullDatasetError: unknown;
}

export const patientFamilyHistoriesAdapter = createEntityAdapter<PatientFamilyHistory>({
  selectId: patientFamilyHistory => patientFamilyHistory.familyMemberTypeId,
});

export const initialState: PatientFamilyHistoriesState = patientFamilyHistoriesAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  pageSize: 5,
  isFullDatasetLoaded: false,
  cachePatientId: null,
  cacheLoadedAt: null,
  isFullDatasetLoading: false,
  fullDatasetError: null,
});

export const patientFamilyHistoriesFeatureKey = 'patient-family-histories';

export const patientFamilyHistoriesReducer = createReducer(
  initialState,
  on(familyActions.getPatientFamilyHistories, getHealthSummary, (state): PatientFamilyHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
      fullDatasetError: null,
    };
  }),

  on(
    familyActions.getPatientFamilyHistoriesSuccess,
    (state, { patientFamilyHistories, pageNumber, isAtEndOfData, totalRecordCount }): PatientFamilyHistoriesState => {
      const nextState =
        pageNumber <= 1
          ? patientFamilyHistoriesAdapter.setAll(patientFamilyHistories, state)
          : patientFamilyHistoriesAdapter.addMany(patientFamilyHistories, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          isLoading: false,
          pageNumber,
          isAtEndOfData,
          totalRecordCount,
        },
      };
    }
  ),

  on(familyActions.getPatientFamilyHistoriesFailure, (state, { error }): PatientFamilyHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      fullDatasetError: null,
    };
  }),

  on(
    familyActions.getAllPatientFamilyHistories,
    (state): PatientFamilyHistoriesState => ({
      ...state,
      isFullDatasetLoading: true,
      fullDatasetError: null,
    })
  ),

  on(
    familyActions.getAllPatientFamilyHistoriesSuccess,
    (state, { patientFamilyHistories, patientId, loadedAt }): PatientFamilyHistoriesState => {
      const nextState = patientFamilyHistoriesAdapter.setAll(patientFamilyHistories, state);
      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          error: null,
          isLoading: false,
          pageNumber: 1,
          isAtEndOfData: true,
          totalRecordCount: patientFamilyHistories.length,
        },
        isFullDatasetLoaded: true,
        cachePatientId: patientId,
        cacheLoadedAt: loadedAt,
        isFullDatasetLoading: false,
        fullDatasetError: null,
      };
    }
  ),

  on(
    familyActions.getAllPatientFamilyHistoriesFailure,
    (state, { error }): PatientFamilyHistoriesState => ({
      ...state,
      isFullDatasetLoading: false,
      fullDatasetError: error,
    })
  ),

  on(
    familyActions.invalidateFamilyHistoryCache,
    (state): PatientFamilyHistoriesState => ({
      ...state,
      isFullDatasetLoaded: false,
      cachePatientId: null,
      cacheLoadedAt: null,
      isFullDatasetLoading: false,
      fullDatasetError: null,
    })
  ),

  on(familyActions.setPageNumber, (state, { pageNumber }): PatientFamilyHistoriesState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        pageNumber,
      },
    };
  }),

  on(
    setGlobalPageSize,
    (state, { pageSize }): PatientFamilyHistoriesState => ({
      ...state,
      pageSize,
    })
  )
);
